import { Vector2 } from 'three';

/**
 *  Returns ane edge (v0-v1, v1-v2 or v2-v0) that is closest to the specified point.
 *
 * @param point - Point to compare the triangle edges towards.
 * @param v1 - Triangle point 1
 * @param v2 - Triangle point 2
 * @param v3 - Triangle point 3
 */
export const findClosestTriangleEdge = (
  point: Vector2,
  v0: Vector2,
  v1: Vector2,
  v2: Vector2,
): { v0: Vector2; v1: Vector2; index: [number, number] } | undefined => {
  const middlePoint = new Vector2(v0.x + v1.x + v2.x, v0.y + v1.y + v2.y).multiplyScalar(1 / 3);

  const edges = [
    [middlePoint, v1, v2],
    [middlePoint, v2, v0],
    [middlePoint, v0, v1],
  ];

  for (const [index, edge] of edges.entries()) {
    const line1 = edge[1].clone().sub(edge[0]).normalize();
    const line2 = edge[2].clone().sub(edge[0]).normalize();
    if (
      pointInTriangle(
        point,
        edge[0],
        edge[0].clone().add(line1.multiplyScalar(100000)),
        edge[0].clone().add(line2.multiplyScalar(100000)),
      )
    ) {
      return { v0: edge[1], v1: edge[2], index: [(index + 1) % 3, (index + 2) % 3] };
    }
  }
};

/**
 *  Calculates the shortest distance from a point to a line segment.
 *
 * @param point - Point to calculate the triangle distance towards.
 * @param linePoint1 - Line point 1
 * @param linePoint2 - Line point 2
 */
export const distanceToLineSegment = (point: Vector2, linePoint1: Vector2, linePoint2: Vector2) => {
  const vectorToPoint = point.clone().sub(linePoint1);
  const lineVector = linePoint2.clone().sub(linePoint1);

  const dot = vectorToPoint.dot(lineVector);
  const len_sq = lineVector.lengthSq();

  // Handle division by zero and limit the range between 0-1.
  const param = Math.max(0, Math.min(1, len_sq === 0 ? -1 : dot / len_sq));
  const edgePoint = linePoint1.clone().add(lineVector.multiplyScalar(param));

  return edgePoint.sub(point).length();
};

/**
 *  Returns true if a point is within a triangle in 2D
 */
export const pointInTriangle = (pt: Vector2, v1: Vector2, v2: Vector2, v3: Vector2) => {
  const sign = (p1: Vector2, p2: Vector2, p3: Vector2): number => {
    return (p1.x - p3.x) * (p2.y - p3.y) - (p2.x - p3.x) * (p1.y - p3.y);
  };

  const d1 = sign(pt, v1, v2);
  const d2 = sign(pt, v2, v3);
  const d3 = sign(pt, v3, v1);
  return !((d1 < 0 || d2 < 0 || d3 < 0) && (d1 > 0 || d2 > 0 || d3 > 0));
};
